import { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, Field, GoogleButton } from "@/components/AuthShell";
import { fmtError } from "@/lib/api";
import { Loader2, Phone, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function SignIn() {
  const { login, requestOtp, verifyOtp, config } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const phoneEnabled = config?.phone_otp_enabled !== false;
  const demoOtp = config?.demo_mode !== false; // Preview/test build shows OTP on screen
  const [method, setMethod] = useState("phone");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (!phoneEnabled && method === "phone") setMethod("email"); }, [phoneEnabled, method]);

  const goNext = (user) => {
    const dest = location.state?.from?.pathname;
    navigate(user.onboarding_completed ? (dest && dest !== "/auth/sign-in" ? dest : "/home") : "/onboarding", { replace: true });
  };

  // ---- phone OTP ----
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [step, setStep] = useState("phone");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);
  const startCooldown = (secs) => {
    setCooldown(secs);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; }), 1000);
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    setError(""); setBusy(true);
    try {
      const data = await requestOtp(phone);
      setStep("code");
      startCooldown(data.resend_after || 30);
      if (data.dev_otp) toast.info(`TEST OTP: ${data.dev_otp}`, { duration: 8000 });
      else toast.success("OTP sent to your phone");
    } catch (err) { setError(fmtError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const confirmOtp = async (e) => {
    e?.preventDefault();
    setError(""); setBusy(true);
    try {
      const data = await verifyOtp(phone, code, name);
      goNext(data.user);
    } catch (err) { setError(fmtError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  // ---- email/password ----
  const [form, setForm] = useState({ email: "", password: "" });
  const submitEmail = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { goNext(await login(form.email, form.password)); }
    catch (err) { setError(fmtError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your neighbourhood response network.">
      {phoneEnabled && (
        <div className="flex gap-2 bg-[#111827]/5 rounded-2xl p-1.5 mb-6" data-testid="signin-method-tabs">
          <button data-testid="method-phone" onClick={() => { setMethod("phone"); setError(""); }} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-colors ${method === "phone" ? "bg-white shadow-sm text-[#111827]" : "text-[#111827]/50"}`}><Phone className="w-4 h-4" /> Phone</button>
          <button data-testid="method-email" onClick={() => { setMethod("email"); setError(""); }} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-colors ${method === "email" ? "bg-white shadow-sm text-[#111827]" : "text-[#111827]/50"}`}><Mail className="w-4 h-4" /> Email</button>
        </div>
      )}

      {phoneEnabled && method === "phone" && step === "phone" && (
        <form onSubmit={sendOtp} className="ll-fade-up">
          <Field label="Mobile number" testId="signin-phone" type="tel" inputMode="numeric" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" />
          <Field label="Your name (new users)" testId="signin-otp-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Anita Sharma" />
          {error && <p data-testid="signin-error" className="text-[#DC2626] text-sm font-medium mb-4 bg-[#DC2626]/10 rounded-xl p-3">{error}</p>}
          <button type="submit" disabled={busy} data-testid="send-otp-btn" className="w-full bg-[#F97316] text-white rounded-2xl py-4 px-6 font-bold text-lg shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-transform active:scale-95 min-h-[56px] flex items-center justify-center disabled:opacity-70">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send OTP"}
          </button>
          <p data-testid="test-otp-note" className="text-center text-xs text-[#111827]/45 mt-3">{demoOtp ? "Test build — the 6-digit code appears on screen. No SMS is sent." : "We'll text you a 6-digit verification code."}</p>
        </form>
      )}

      {phoneEnabled && method === "phone" && step === "code" && (
        <form onSubmit={confirmOtp} className="ll-fade-up">
          <button type="button" onClick={() => { setStep("phone"); setCode(""); setError(""); }} data-testid="otp-back" className="flex items-center gap-1.5 text-sm font-semibold text-[#111827]/60 mb-3"><ArrowLeft className="w-4 h-4" /> Change number</button>
          <p className="text-sm text-[#111827]/70 mb-4">Enter the 6-digit code sent to <span className="font-bold">{phone}</span></p>
          <Field label="OTP code" testId="signin-otp-code" type="tel" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
          {error && <p data-testid="signin-error" className="text-[#DC2626] text-sm font-medium mb-4 bg-[#DC2626]/10 rounded-xl p-3">{error}</p>}
          <button type="submit" disabled={busy || code.length < 6} data-testid="verify-otp-btn" className="w-full bg-[#F97316] text-white rounded-2xl py-4 px-6 font-bold text-lg shadow-sm hover:-translate-y-0.5 transition-transform active:scale-95 min-h-[56px] flex items-center justify-center disabled:opacity-60">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify & continue"}
          </button>
          <button type="button" disabled={cooldown > 0 || busy} onClick={sendOtp} data-testid="resend-otp-btn" className="w-full mt-3 text-sm font-semibold text-[#F97316] disabled:text-[#111827]/40">
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
        </form>
      )}

      {(!phoneEnabled || method === "email") && (
        <form onSubmit={submitEmail} className="ll-fade-up">
          <Field label="Email" testId="signin-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
          <Field label="Password" testId="signin-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
          <div className="flex justify-end -mt-1 mb-4">
            <Link to="/auth/forgot-password" data-testid="forgot-password-link" className="text-sm font-semibold text-[#F97316]">Forgot password?</Link>
          </div>
          {error && <p data-testid="signin-error" className="text-[#DC2626] text-sm font-medium mb-4 bg-[#DC2626]/10 rounded-xl p-3">{error}</p>}
          <button type="submit" disabled={busy} data-testid="signin-submit" className="w-full bg-[#F97316] text-white rounded-2xl py-4 px-6 font-bold text-lg shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-transform active:scale-95 min-h-[56px] flex items-center justify-center disabled:opacity-70">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
          </button>
        </form>
      )}

      <div className="flex items-center gap-3 my-6"><div className="h-px flex-1 bg-[#111827]/10" /><span className="text-xs text-[#111827]/40 font-semibold uppercase">or</span><div className="h-px flex-1 bg-[#111827]/10" /></div>
      <GoogleButton />
      <p className="text-center mt-8 text-[#111827]/70">New here? <Link to="/auth/sign-up" data-testid="go-signup" className="font-bold text-[#F97316]">Create an account</Link></p>
    </AuthShell>
  );
}
